import React, { useState } from 'react';
import { WhatsAppMessage, PLACEHOLDER_AVATAR_SVG, VCardContact, ContactActionHandlers } from './types';
import {
    TrashIcon, PhoneIcon, MapPinIcon, UserIcon, InformationCircleIcon,
    DocumentTextIcon, CheckIcon, CheckCircleIcon, PlayCircleIcon, ArrowDownCircleIcon,
    ClockIcon, ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import ContactCard from './components/ContactCard';

// Define a callback type for image viewing
export type ImageViewCallback = (imageUrl: string, caption?: string, filename?: string) => void;

const MEDIA_DOWNLOAD_LOADING_TIMEOUT_MS = 190000;

// Helper to check if messages are on different days
export const isDifferentDay = (ts1: number, ts2: number): boolean => {
    const date1 = new Date(ts1);
    const date2 = new Date(ts2);
    return date1.toDateString() !== date2.toDateString();
};

export function parseVCard(vcardString: string): VCardContact {
    const contact: VCardContact = { rawVCard: vcardString };
    
    console.log('🔍 parseVCard: Raw vCard string:', vcardString);
    
    // Extract name from FN field
    const fnMatch = vcardString.match(/FN:([^\n\r]+)/);
    if (fnMatch) {
        contact.name = fnMatch[1].trim();
        console.log('✅ parseVCard: Found name from FN:', contact.name);
    }
    
    // Extract name from N field (fallback)
    if (!contact.name) {
        const nMatch = vcardString.match(/N:([^\n\r]+)/);
        if (nMatch) {
            const nameParts = nMatch[1].split(';').filter(part => part.trim());
            if (nameParts.length > 0) {
                contact.name = nameParts.join(' ').trim();
                console.log('✅ parseVCard: Found name from N:', contact.name);
            }
        }
    }
    
    const waMatch = vcardString.match(/waid=(\d+)/);
    if (waMatch) {
        contact.phone = waMatch[1];
        console.log('✅ parseVCard: Found WhatsApp phone (waid):', contact.phone);
    } 

    else {
        const telMatch = vcardString.match(/TEL[^:]*:([+\d\s\-().]+)/);
        if (telMatch) {
            contact.phone = telMatch[1].trim().replace(/\s+/g, '');
            console.log('✅ parseVCard: Found fallback phone:', contact.phone);
        }
    }
    
    // Extract email
    const emailMatch = vcardString.match(/EMAIL[^:]*:([^\s\n]+)/);
    if (emailMatch) {
        contact.email = emailMatch[1].trim();
        console.log('✅ parseVCard: Found email:', contact.email);
    }
    
    // Extract organization
    const orgMatch = vcardString.match(/ORG:([^\n]+)/);
    if (orgMatch) {
        contact.org = orgMatch[1].trim();
        console.log('✅ parseVCard: Found org:', contact.org);
    }
    
    // Extract title
    const titleMatch = vcardString.match(/TITLE:([^\n]+)/);
    if (titleMatch) {
        contact.title = titleMatch[1].trim();
        console.log('✅ parseVCard: Found title:', contact.title);
    }
    
    console.log('🎯 parseVCard: Final parsed contact:', contact);
    return contact;
}

export function createVCard(contact: VCardContact): string {
    const lines = ['BEGIN:VCARD', 'VERSION:3.0'];
    
    if (contact.name) lines.push(`FN:${contact.name}`);
    if (contact.phone) lines.push(`TEL:${contact.phone}`);
    if (contact.email) lines.push(`EMAIL:${contact.email}`);
    if (contact.org) lines.push(`ORG:${contact.org}`);
    if (contact.title) lines.push(`TITLE:${contact.title}`);
    
    lines.push('END:VCARD');
    return lines.join('\n');
}

export function formatPhoneNumber(phone: string): string {
    if (!phone) return '';
    
    const cleaned = phone.replace(/[^\d+]/g, '');
    
    if (cleaned.startsWith('+7') && cleaned.length === 12) {
        return `+7 (${cleaned.substring(2, 5)}) ${cleaned.substring(5, 8)}-${cleaned.substring(8, 10)}-${cleaned.substring(10, 12)}`;
    }
    
    if (cleaned.startsWith('+') && cleaned.length > 7) {
        const countryCode = cleaned.substring(0, 3);
        const rest = cleaned.substring(3);
        return `${countryCode} ${rest.replace(/(\d{3})(?=\d)/g, '$1 ')}`;
    }
    
    return cleaned;
}

// Determine message bubble position based on surrounding messages and dates
export const getMessagePosition = (
    currentMsg: WhatsAppMessage,
    prevMsg?: WhatsAppMessage,
    nextMsg?: WhatsAppMessage
): 'single' | 'first' | 'normal' | 'last' => {
    const isFirst = !prevMsg || prevMsg.direction !== currentMsg.direction || isDifferentDay(prevMsg.timestamp, currentMsg.timestamp);
    const isLast = !nextMsg || nextMsg.direction !== currentMsg.direction || isDifferentDay(currentMsg.timestamp, nextMsg.timestamp);

    if (isFirst && isLast) return 'single';
    if (isFirst) return 'first';
    if (isLast) return 'last';
    return 'normal';
};

// Simple linkify helper function
export const linkify = (text: string): string => {
    const urlRegex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/ig;
    // Basic email regex (simplified)
    const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi;

    let linkedText = text.replace(urlRegex, (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">${url}</a>`);
    // Avoid linkifying emails within existing anchor tags
    linkedText = linkedText.replace(emailRegex, (email) => {
        // Check if the email is already inside an href attribute
        const surroundingText = linkedText.substring(linkedText.indexOf(email) - 10, linkedText.indexOf(email) + email.length + 10);
        if (surroundingText.includes('href="mailto:') || surroundingText.includes('href="http')) {
            return email; // Already linked or part of a URL
        }
        return `<a href="mailto:${email}" class="text-blue-600 hover:underline">${email}</a>`;
    });
    return linkedText;
};

const DEFAULT_DOCUMENT_MIME_TYPE = 'application/octet-stream';

const getCleanMimeType = (mimetype?: string, fallback = DEFAULT_DOCUMENT_MIME_TYPE): string => {
    return mimetype?.split(';')[0] || fallback;
};

const createDataUri = (
    base64Data?: string,
    mimetype?: string,
    fallback = DEFAULT_DOCUMENT_MIME_TYPE
): string | undefined => {
    if (!base64Data) return undefined;
    return `data:${getCleanMimeType(mimetype, fallback)};base64,${base64Data}`;
};

const dataUriToBlob = (dataUri: string, fallbackMimeType = DEFAULT_DOCUMENT_MIME_TYPE): Blob => {
    const [metadata, base64Data = ''] = dataUri.split(',');
    const mimeMatch = metadata.match(/^data:([^;]+);base64$/);
    const mimeType = mimeMatch?.[1] || fallbackMimeType;
    const byteCharacters = atob(base64Data);
    const byteArrays: Uint8Array[] = [];
    const sliceSize = 1024;

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);
        const byteNumbers = new Array(slice.length);

        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        byteArrays.push(new Uint8Array(byteNumbers));
    }

    return new Blob(byteArrays, { type: mimeType });
};

const openDataUriInNewTab = (dataUri: string, fallbackMimeType = DEFAULT_DOCUMENT_MIME_TYPE): void => {
    const blob = dataUriToBlob(dataUri, fallbackMimeType);
    const url = URL.createObjectURL(blob);
    const newWindow = window.open(url, '_blank');

    if (newWindow) {
        newWindow.onload = () => URL.revokeObjectURL(url);
    } else {
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
};

const downloadDataUri = (
    dataUri: string,
    filename = 'document',
    fallbackMimeType = DEFAULT_DOCUMENT_MIME_TYPE
): void => {
    const blob = dataUriToBlob(dataUri, fallbackMimeType);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const getFileSizeFromDataUri = (dataUri: string): string => {
    try {
        const base64 = dataUri.split(',')[1] || '';
        const bytes = Math.floor((base64.length * 3) / 4);
        const sizes = ['Б', 'КБ', 'МБ', 'ГБ'];
        if (bytes === 0) return '0 Б';
        const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1);
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    } catch {
        return 'неизвестно';
    }
};

const isSpreadsheetFile = (mimetype?: string, filename?: string): boolean => {
    const cleanMimeType = getCleanMimeType(mimetype, '');

    return (
        cleanMimeType.includes('spreadsheet') ||
        cleanMimeType.includes('excel') ||
        cleanMimeType === 'application/vnd.ms-excel' ||
        cleanMimeType === 'text/csv' ||
        /\.(xlsx|xls|xlsm|xlsb|csv)$/i.test(filename || '')
    );
};

const getDocumentDownloadLabel = (mimetype?: string, filename?: string): string => {
    const cleanMimeType = getCleanMimeType(mimetype, '');

    if (isSpreadsheetFile(cleanMimeType, filename)) return 'Скачать Excel';
    if (cleanMimeType === 'application/pdf') return 'Скачать PDF';
    if (cleanMimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'Скачать DOCX';
    return 'Скачать файл';
};

const getDocumentFormatLabel = (mimetype?: string, filename?: string): string => {
    const cleanMimeType = getCleanMimeType(mimetype, '');

    if (isSpreadsheetFile(cleanMimeType, filename)) return 'Excel';
    if (cleanMimeType === 'application/pdf') return 'PDF';
    if (cleanMimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'DOCX';
    return cleanMimeType || 'файл';
};

const getDocumentFallbackFilename = (mimetype?: string, filename?: string): string => {
    if (filename) return filename;
    if (isSpreadsheetFile(mimetype, filename)) return 'document.xlsx';
    if (getCleanMimeType(mimetype, '') === 'application/pdf') return 'document.pdf';
    if (getCleanMimeType(mimetype, '') === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'document.docx';
    return 'document';
};

const PDFPreview: React.FC<{ dataUri: string; filename?: string }> = ({ dataUri, filename }) => {
  const handleViewPDF = () => {
    openDataUriInNewTab(dataUri, 'application/pdf');
  };

  const handleDownload = () => {
    downloadDataUri(dataUri, filename || 'document.pdf', 'application/pdf');
  };

  return (
    <div className="flex items-center space-x-2 p-2 bg-gray-100 rounded-lg max-w-xs">
      <DocumentTextIcon className="h-8 w-8 text-red-500 flex-shrink-0" />
      <div className="flex-grow overflow-hidden">
        <p className="text-sm font-medium truncate">{filename || 'Document.pdf'}</p>
        <div className="flex space-x-2 mt-1">
          <button
            onClick={handleViewPDF}
            className="text-xs text-blue-600 hover:underline"
          >
            Открыть PDF
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleDownload();
            }}
            className="text-xs text-green-600 hover:underline"
          >
            Скачать
          </button>
        </div>
      </div>
    </div>
  );
};

const DOCXPreview: React.FC<{ dataUri: string; filename?: string }> = ({ dataUri, filename }) => {
  const handleDownload = () => {
    try {
      downloadDataUri(
        dataUri,
        filename || 'document.docx',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      );
      
      if (typeof window !== 'undefined' && (window as any).toast) {
        (window as any).toast.success('Файл начал загрузку');
      }
    } catch (error) {
      console.error('Download failed:', error);
      window.open(dataUri, '_blank');
    }
  };

  const getFileSize = () => {
    return getFileSizeFromDataUri(dataUri);
  };

  return (
    <div className="flex items-center space-x-3 p-3 bg-blue-50 border border-blue-200 rounded-lg max-w-md">
      <div className="flex-shrink-0">
        <DocumentTextIcon className="h-10 w-10 text-blue-600" />
      </div>
      <div className="flex-grow overflow-hidden">
        <p className="text-sm font-semibold text-gray-800 truncate">
          {filename || 'Документ Word'}
        </p>
        <p className="text-xs text-gray-600 mt-1">
          Формат DOCX • Размер: {getFileSize()}
        </p>
        <div className="flex space-x-2 mt-3">
          <button 
            onClick={handleDownload}
            className="flex items-center space-x-1 text-xs bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowDownCircleIcon className="h-4 w-4" />
            <span>Скачать DOCX</span>
          </button>
        </div>
        <p className="text-xs text-blue-600 mt-2">
          💡 Скачайте файл для просмотра в Microsoft Word или другом редакторе
        </p>
      </div>
    </div>
  );
};

// Helper component for lazy-loading media on click
interface MediaPlaceholderProps {
    msg: WhatsAppMessage;
    onRequestMedia: (messageId: string) => void; // Function to request media download
}

const MediaPlaceholder: React.FC<MediaPlaceholderProps> = ({ msg, onRequestMedia }) => {
    const [isLoading, setIsLoading] = useState(false); // State to track loading after request
    const cleanMimeType = getCleanMimeType(msg.mimetype);
    const dataUri = createDataUri(msg.mediaData, msg.mimetype);
    const downloadFilename = getDocumentFallbackFilename(msg.mimetype, msg.filename);
    const downloadLabel = getDocumentDownloadLabel(msg.mimetype, msg.filename);
    const formatLabel = getDocumentFormatLabel(msg.mimetype, msg.filename);

    React.useEffect(() => {
        if (!isLoading) return;

        if (!msg.mediaRequiresDownload || msg.error || msg.mediaData) {
            setIsLoading(false);
        }
    }, [isLoading, msg.mediaRequiresDownload, msg.error, msg.mediaData]);

    React.useEffect(() => {
        if (!isLoading) return;

        const timeoutId = window.setTimeout(() => {
            setIsLoading(false);
        }, MEDIA_DOWNLOAD_LOADING_TIMEOUT_MS);

        return () => window.clearTimeout(timeoutId);
    }, [isLoading, msg.id]);

    const handleLoadClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        // Only request if not already loading and media is marked as requiring download
        console.log(`[MediaPlaceholder] handleLoadClick triggered for msg ID: ${msg.id}. isLoading: ${isLoading}, requiresDownload: ${msg.mediaRequiresDownload}`);
        if (!isLoading && msg.mediaRequiresDownload && msg.id) {
            console.log(`[MediaPlaceholder] Calling onRequestMedia for msg ID: ${msg.id}`);
            setIsLoading(true);
            onRequestMedia(msg.id); // Call the passed function to trigger backend download
        } else if (dataUri && !msg.mediaRequiresDownload) {
            // If data already exists (e.g., after download), maybe open it directly?
            // Or do nothing if it's already displayed. For now, do nothing.
            console.log("Media already loaded or doesn't require download.");
        } else if (!msg.id) {
            console.error("Cannot request media: Message ID is missing.");
        }
    };

    // Determine if media is ready to be displayed
    const isMediaReady = dataUri && !msg.mediaRequiresDownload;

    const handleDocumentPreview = () => {
        if (!dataUri) return;

        if (cleanMimeType === 'application/pdf') {
            openDataUriInNewTab(dataUri, 'application/pdf');
        } else if (cleanMimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
            const blob = dataUriToBlob(dataUri, cleanMimeType);
            const url = URL.createObjectURL(blob);
            const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
            const newWindow = window.open(viewerUrl, '_blank');
            
            if (newWindow) {
                newWindow.onload = () => URL.revokeObjectURL(url);
            } else {
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            }
        }
    };

    const handleDownloadDocument = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!dataUri) return;

        downloadDataUri(dataUri, downloadFilename, cleanMimeType);
    };

    // 1. Render actual media if it's ready
    if (isMediaReady) {
        if (cleanMimeType === 'application/pdf' || cleanMimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
            return (
                <div className="flex items-center space-x-2 p-2 bg-gray-100 rounded-lg max-w-xs">
                    <DocumentTextIcon className="h-6 w-6 text-gray-600 flex-shrink-0" />
                    <div className="flex-grow overflow-hidden">
                        <p className="text-sm font-medium truncate">{msg.filename || 'Документ'}</p>
                        <div className="flex space-x-2 mt-1">
                            <button
                                onClick={handleDocumentPreview}
                                className="text-xs text-blue-600 hover:underline"
                            >
                                {cleanMimeType === 'application/pdf' ? 'Открыть PDF' : 'Просмотреть DOCX'}
                            </button>
                            <button
                                type="button"
                                onClick={handleDownloadDocument}
                                className="text-xs text-green-600 hover:underline"
                            >
                                Скачать
                            </button>
                        </div>
                        {msg.message && <p className="text-xs mt-1" dangerouslySetInnerHTML={{ __html: linkify(msg.message) }} />}
                    </div>
                </div>
            );
        }

        switch (msg.type) {
            case 'video':
                return (
                    <div className="max-w-xs">
                        {/* Added autoPlay */}
                        <video controls autoPlay src={dataUri} className="rounded-lg max-h-60">Ваш браузер не поддерживает тег video.</video>
                        {msg.message && <p className="text-sm mt-1" dangerouslySetInnerHTML={{ __html: linkify(msg.message) }} />}
                    </div>
                );
            case 'audio':
            case 'ptt':
                return (
                    <div className="max-w-xs">
                        <audio controls src={dataUri}>
                            Ваш браузер не поддерживает элемент audio.
                        </audio>
                        {msg.message && <p className="text-sm mt-1" dangerouslySetInnerHTML={{ __html: linkify(msg.message) }} />}
                    </div>
                );
            // Handle UNKNOWN/OVERSIZED here if they should behave like documents
            case 'document':
            case 'unknown':
            case 'oversized':
                return (
                    <div className="flex items-center space-x-2 p-2 bg-gray-100 rounded-lg max-w-xs">
                        <DocumentTextIcon className="h-6 w-6 text-gray-600 flex-shrink-0" />
                        <div className="flex-grow overflow-hidden">
                            <p className="text-sm font-medium ">{msg.filename || 'Документ'}</p>
                            {/* Make the download link more prominent */}
                            <button
                                type="button"
                                onClick={handleDownloadDocument}
                                className="text-sm font-semibold text-blue-700 hover:underline block mt-1 text-left"
                            >
                                {downloadLabel} ({formatLabel})
                            </button>
                            {msg.message && <p className="text-xs mt-1" dangerouslySetInnerHTML={{ __html: linkify(msg.message) }} />}
                        </div>
                    </div>
                );
            default:
                // Fallback for unexpected types passed to placeholder
                return <div className="text-red-500 text-xs italic">Неподдерживаемый тип для предпросмотра</div>;
        }
    }

    // 2. Render placeholder (clickable or loading state)
    const isAudio = msg.type === 'audio' || msg.type === 'ptt';
    const isImage = msg.type === 'image' || msg.type === 'sticker';
    const Icon = msg.type === 'video' || isAudio ? PlayCircleIcon : ArrowDownCircleIcon;
    const label = msg.type === 'video'
        ? 'Загрузить видео'
        : (isAudio ? 'Загрузить аудио' : (isImage ? 'Загрузить изображение' : 'Загрузить документ'));
    const filename = msg.filename || (msg.type === 'video'
        ? 'Видео'
        : (isAudio ? 'Аудио' : (isImage ? 'Изображение' : (msg.type === 'document' ? 'Документ' : 'Файл')))); // More generic fallback
    // Estimate file size (very rough, assumes base64 overhead)
    const estimatedSizeMB = msg.mediaData ? (msg.mediaData.length * 3 / 4 / (1024 * 1024)).toFixed(2) : null;

    return (
        <div
            className="flex items-center space-x-2 p-2 bg-gray-100 rounded-lg max-w-xs cursor-pointer hover:bg-gray-200 transition-colors duration-150"
            onClick={handleLoadClick}
            title={isLoading ? "Загрузка..." : `Нажмите, чтобы загрузить ${filename}${estimatedSizeMB ? ` (~${estimatedSizeMB} MB)` : ''}`}
            style={{ cursor: isLoading ? 'default' : 'pointer' }} // Change cursor when loading
        >
            {isLoading ? (
                <svg className="animate-spin h-8 w-8 text-blue-500 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            ) : (
                <Icon className="h-8 w-8 text-gray-500 flex-shrink-0" />
            )}
            <div className="flex-grow overflow-hidden">
                <p className="text-sm font-medium ">{filename}</p>
                <p className={`text-xs ${isLoading ? 'text-gray-500' : 'text-blue-600 hover:underline'}`}>{isLoading ? 'Загрузка...' : label}</p>
                {msg.error && !isLoading && <p className="text-xs text-red-500 mt-1">{msg.error}</p>}
                {/* Show estimated size if available */}
                {estimatedSizeMB && !isLoading && <p className="text-xs text-gray-500">~{estimatedSizeMB} MB</p>}
                {/* Display caption below the placeholder */}
                {msg.message && <p className="text-xs mt-1 text-gray-700" dangerouslySetInnerHTML={{ __html: linkify(msg.message) }} />}
            </div>
        </div>
    );
};


// Helper function to render a preview of the quoted message
const renderQuotedMessagePreview = (
    quotedMsg: WhatsAppMessage,
    onQuotedMessageClick?: (messageId: string) => void
): React.ReactNode => {
    if (!quotedMsg) return null;

    const handlePreviewClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent triggering other click events if nested
        if (onQuotedMessageClick && quotedMsg.id) {
            onQuotedMessageClick(quotedMsg.id);
        }
    };

    // Determine the preview text/content
    let previewContent: React.ReactNode = quotedMsg.message || '';
    if (quotedMsg.hasMedia) {
        switch (quotedMsg.type) {
            case 'image': previewContent = '📷 Изображение'; break;
            case 'video': previewContent = '📹 Видео'; break;
            case 'audio':
            case 'ptt': previewContent = '🎵 Аудио'; break;
            case 'document': previewContent = `📄 ${quotedMsg.filename || 'Документ'}`; break;
            case 'sticker': previewContent = 'Стикер'; break;
            case 'vcard': 
                const contactName = getContactName(quotedMsg.vcard);
                previewContent = `👤 Контакт: ${contactName}`;
                break;
            default: previewContent = '📎 Вложение'; break;
        }
        // Append caption if it exists for media
        if (quotedMsg.message) {
            previewContent = <>{previewContent}: <span className="italic opacity-80">{quotedMsg.message}</span></>;
        }
    } else if (quotedMsg.type === 'location') {
        previewContent = `📍 Местоположение ${quotedMsg.location?.description ? `: ${quotedMsg.location.description}` : ''}`;
    } else if (quotedMsg.type === 'vcard') {
        const contactName = getContactName(quotedMsg.vcard);
        previewContent = `👤 Контакт: ${contactName}`;
    } else if (quotedMsg.type === 'call_log') {
        previewContent = `📞 ${quotedMsg.message || 'Журнал вызовов'}`;
    }

    // Basic styling for the quoted block
    return (
        <div
            className={`mb-1 p-2 border-l-4 border-blue-400 bg-gray-100 dark:bg-gray-700 rounded-r-md text-sm opacity-90 ${onQuotedMessageClick ? 'cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600' : ''}`}
            onClick={onQuotedMessageClick ? handlePreviewClick : undefined}
            title={onQuotedMessageClick ? "Перейти к сообщению" : ""}
        >
            <div className="font-semibold text-blue-600 dark:text-blue-400 text-xs mb-0.5">
                {quotedMsg.sender || 'Неизвестно'} {/* Use sender instead of senderName */}
            </div>
            <div className="text-gray-700 dark:text-gray-300 ">
                {/* Render simple text or the generated preview node */}
                {typeof previewContent === 'string' ? <span dangerouslySetInnerHTML={{ __html: linkify(previewContent) }} /> : previewContent}
            </div>
        </div>
    );
};


// Helper function to render different message types
// Now accepts onRequestMedia function to pass down and onImageView for opening images
export const renderMessageContent = (
    msg: WhatsAppMessage,
    onRequestMedia: (messageId: string) => void,
    onImageView?: ImageViewCallback,
    onQuotedMessageClick?: (messageId: string) => void, // Add new prop
    contactHandlers?: ContactActionHandlers
): React.ReactNode => {
    // dataUri is now primarily handled within MediaPlaceholder for lazy types
    // We still need it for non-lazy types like image, audio, sticker
    const cleanMimeType = getCleanMimeType(msg.mimetype);
    const dataUri = createDataUri(msg.mediaData, msg.mimetype);


    if (msg.error && !msg.mediaRequiresDownload) { // Don't show backend download error if placeholder is shown
        return <div className="text-red-500 text-xs italic">Ошибка: {msg.error}</div>;
    }

    // Main content rendering logic based on type
    let mainContent: React.ReactNode;
    switch (msg.type) {
        case 'revoked':
            return (
                <div className="flex items-center space-x-1 text-gray-500 italic text-sm">
                    <TrashIcon className="h-4 w-4 flex-shrink-0" />
                    <span>{msg.direction === 'outgoing' ? 'Вы удалили это сообщение' : 'Это сообщение было удалено'}</span>
                </div>
            );
            break; // Use break instead of return inside switch
        case 'call_log':
            // Assuming msg.message contains call details
            mainContent = (
                <div className="flex items-center space-x-1 text-gray-600 text-sm">
                    <PhoneIcon className="h-4 w-4 flex-shrink-0" />
                    <span>Журнал вызовов: {msg.message}</span>
                </div>
            );
            break;
        case 'location':
            const mapsUrl = `https://www.google.com/maps?q=${msg.location?.latitude},${msg.location?.longitude}`;
            mainContent = (
                <div className="max-w-xs p-2 border rounded-lg">
                    <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 group">
                        <MapPinIcon className="h-8 w-8 text-red-500 flex-shrink-0 group-hover:text-red-700" />
                        <div className="overflow-hidden">
                            <p className="text-sm font-medium text-blue-600 group-hover:underline">Местоположение</p>
                            {msg.location?.description && <p className="text-xs text-gray-600 ">{msg.location.description}</p>}
                            <p className="text-xs text-gray-500">{msg.location?.latitude?.toFixed(5)}, {msg.location?.longitude?.toFixed(5)}</p>
                        </div>
                    </a>
                </div>
            );
            break;
        case 'vcard':
            console.log('🔍 VCard Debug - Raw vcard data:', {
                msgVcard: msg.vcard,
                msgParsedVCard: msg.parsedVCard,
                msgType: typeof msg.vcard
            });

            let contactToDisplay: VCardContact | undefined;

            if (msg.parsedVCard) {
                contactToDisplay = msg.parsedVCard;
                console.log('✅ Using parsedVCard:', contactToDisplay);
            } else if (typeof msg.vcard === 'string') {
                console.log('🔄 Parsing vcard string...');
                contactToDisplay = parseVCard(msg.vcard);
                console.log('✅ Parsed vcard:', contactToDisplay);
            } else if (msg.vcard && typeof msg.vcard === 'object') {
                contactToDisplay = msg.vcard;
                console.log('✅ Using vcard object:', contactToDisplay);
            }

            console.log('🎯 Final contact to display:', contactToDisplay);

            if (contactToDisplay) {
                console.log('Rendering ContactCard with handlers:', {
                    hasOnCreateChat: !!contactHandlers?.onCreateChat,
                    contact: contactToDisplay
                });
                
                return (
                    <ContactCard
                        contact={contactToDisplay}
                        compact={false}
                        onCall={contactHandlers?.onCall}
                        onMessage={contactHandlers?.onMessage}
                        onEmail={contactHandlers?.onEmail}
                        onSaveContact={contactHandlers?.onSaveContact}
                        onViewContact={contactHandlers?.onViewContact}
                        onCreateChat={contactHandlers?.onCreateChat}
                    />
                );
            } else {
                mainContent = (
                    <div className="flex items-center space-x-2 p-2 bg-gray-100 rounded-lg max-w-xs">
                        <UserIcon className="h-6 w-6 text-gray-600 flex-shrink-0" />
                        <div className="flex-grow overflow-hidden">
                            <p className="text-sm font-medium">Контакт</p>
                            <p className="text-xs text-gray-700">Информация о контакте недоступна</p>
                        </div>
                    </div>
                );
            }
            break;
        case 'group_notification':
            // Group notifications are special, render directly and return
            return (
                <div className="flex justify-center my-1">
                    <div className="text-center text-xs text-gray-500 italic bg-gray-100 px-2 py-1 rounded-full shadow-sm max-w-xs break-words">
                        <InformationCircleIcon className="h-3 w-3 inline-block mr-1 align-middle" />
                        <span className="align-middle">{msg.message}</span>
                    </div>
                </div>
            );
        // No break needed as we returned
        case 'image':
            const shouldRequestImageMedia = Boolean(msg.mediaRequiresDownload || msg.error || (msg.hasMedia && !dataUri));
            mainContent = (
                <div className="max-w-xs">
                    {dataUri ? (
                        <div
                            className={onImageView ? "cursor-pointer" : ""}
                            onClick={() => {
                                if (onImageView && dataUri) {
                                    onImageView(dataUri, msg.message, msg.filename);
                                }
                            }}
                            title={onImageView ? "Нажмите для увеличения" : ""}
                        >
                            <img
                                src={dataUri}
                                alt={msg.filename || 'Изображение'}
                                className="rounded-lg object-contain max-h-60"
                            />
                        </div>
                    ) : shouldRequestImageMedia ? (
                        <MediaPlaceholder msg={msg} onRequestMedia={onRequestMedia} />
                    ) : (
                        <div className="text-gray-500 text-xs italic">Загрузка изображения...</div>
                    )}
                    {msg.message && <p className="text-sm mt-1" dangerouslySetInnerHTML={{ __html: linkify(msg.message) }} />}
                </div>
            );
            break;
        case 'video':
            // Use MediaPlaceholder for videos
            mainContent = <MediaPlaceholder msg={msg} onRequestMedia={onRequestMedia} />;
            break;
        case 'audio':
        case 'ptt': // Push-to-talk / Голосовое сообщение
            console.log(`[renderMessageContent] Rendering PTT/Audio: ID=${msg.id}, HasMedia=${msg.hasMedia}, Mimetype=${msg.mimetype}, MediaData Length=${msg.mediaData?.length}`);
            const audioDataUri = createDataUri(msg.mediaData, msg.mimetype, 'audio/ogg');
            console.log(`[renderMessageContent] Generated Audio Data URI with mimetype: ${getCleanMimeType(msg.mimetype, 'audio/ogg')}`);
            if (audioDataUri && !msg.mediaRequiresDownload) {
                return (
                    <div className="flex items-center space-x-2">
                        <audio controls src={audioDataUri}>
                            Ваш браузер не поддерживает элемент audio.
                        </audio>
                        {msg.message && <p className="text-sm mt-1" dangerouslySetInnerHTML={{ __html: linkify(msg.message) }} />}
                    </div>
                );
            }
            mainContent = <MediaPlaceholder msg={msg} onRequestMedia={onRequestMedia} />;
            break;
        case 'document':
            if (cleanMimeType === 'application/pdf') {
                if (dataUri && !msg.mediaRequiresDownload) {
                    mainContent = <PDFPreview dataUri={dataUri} filename={msg.filename} />;
                } else {
                    mainContent = <MediaPlaceholder msg={msg} onRequestMedia={onRequestMedia} />;
                }
            } else if (cleanMimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
                if (dataUri && !msg.mediaRequiresDownload) {
                    mainContent = <DOCXPreview dataUri={dataUri} filename={msg.filename} />;
                } else {
                    mainContent = <MediaPlaceholder msg={msg} onRequestMedia={onRequestMedia} />;
                }
            } else {
                mainContent = <MediaPlaceholder msg={msg} onRequestMedia={onRequestMedia} />;
            }
            break;
        case 'unknown':
        case 'oversized':
            // Use MediaPlaceholder
            mainContent = <MediaPlaceholder msg={msg} onRequestMedia={onRequestMedia} />;
            break;
        case 'sticker':
            // Stickers are usually small
            mainContent = (
                <div className="max-w-[150px]">
                    {dataUri ? (
                        <div
                            className={onImageView ? "cursor-pointer" : ""}
                            onClick={() => {
                                if (onImageView && dataUri) {
                                    onImageView(dataUri, undefined, "sticker.png");
                                }
                            }}
                            title={onImageView ? "Нажмите для увеличения" : ""}
                        >
                            <img src={dataUri} alt="Стикер" className="object-contain" />
                        </div>
                    ) : (
                        <div className="text-gray-500 text-xs italic">Загрузка стикера...</div>
                    )}
                </div>
            );
            break;
        case 'notification_template':
        case 'e2e_notification':
            // These are usually system messages, handle them as needed
            mainContent = <div className="text-gray-500 text-xs italic">{msg.message || 'Системное сообщение'}</div>;
            break;
        case 'chat':
        default:
            if (msg.type !== 'chat') {
                console.warn('Unknown message type: ', msg.type, msg);
            }
            // Default to chat message content
            mainContent = <div dangerouslySetInnerHTML={{ __html: linkify(msg.message || '') }} />;
            break;
    }

    // Combine quoted preview (if exists) and main content
    return (
        <div>
            {msg.hasQuotedMsg && msg.quotedMessage && renderQuotedMessagePreview(msg.quotedMessage, onQuotedMessageClick)}
            {mainContent}
            {(msg.sendStatus === 'pending' || msg.sendStatus === 'failed' || msg.sendStatus === 'unknown') && (
                <div className={`mt-1 text-xs italic ${
                    msg.sendStatus === 'failed'
                        ? 'text-red-600'
                        : msg.sendStatus === 'unknown'
                            ? 'text-amber-600'
                            : 'text-gray-500'
                }`}>
                    {
                        msg.sendStatus === 'failed'
                            ? 'Не отправлено'
                            : msg.sendStatus === 'unknown'
                                ? 'Статус неизвестен'
                                : 'Отправляется…'
                    }
                    {msg.sendError ? `: ${msg.sendError}` : ''}
                </div>
            )}
            {/* Reactions Display */}
            {msg.hasReaction && msg.reactions && msg.reactions.length > 0 && (
                <div className={`flex items-center space-x-1 mt-1 ${msg.direction === 'outgoing' ? 'justify-end' : 'justify-start'}`}>
                    {msg.reactions.map((reaction, index) => (
                        <span key={index} className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-500">
                            {reaction.emoji} {reaction.count > 1 ? <span className="text-xs font-semibold">{reaction.count}</span> : ''}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

// Helper component to render status icons for outgoing messages
export const MessageStatusIndicator: React.FC<{ ack?: number; sendStatus?: WhatsAppMessage['sendStatus'] }> = ({ ack, sendStatus }) => {
    // ack: 0=pending, 1=sent(server), 2=received(device), 3=read(device)
    if (sendStatus === 'failed') {
        return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" title="Не отправлено" />;
    }

    if (sendStatus === 'unknown') {
        return <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" title="Статус неизвестен" />;
    }

    if (ack === 0 || (sendStatus === 'pending' && ack === undefined)) {
        return <ClockIcon className="h-4 w-4 text-gray-400" title="Отправка..." />;
    }

    if (ack === undefined || ack < 1) {
        // Optionally show a pending icon, or null
        // return <ClockIcon className="h-4 w-4 text-gray-400" title="Отправка..." />;
        return null;
    }
    if (ack === 1) { // Sent to server
        return <CheckIcon className="h-4 w-4 text-gray-500" title="Отправлено" />;
    }
    if (ack === 2) { // Received by recipient's device
        // Use double check, still gray
        return (
            <div className="relative w-4 h-4" title="Доставлено">
                <CheckIcon className="h-4 w-4 text-gray-500 absolute" style={{ left: '-2px' }} />
                <CheckIcon className="h-4 w-4 text-gray-500 absolute" style={{ left: '2px' }} />
            </div>
        );
    }
    if (ack >= 3) { // Read by recipient
        // Use double check, blue
        return (
            <div className="relative w-4 h-4" title="Прочитано">
                <CheckIcon className="h-4 w-4 text-blue-500 absolute" style={{ left: '-2px' }} />
                <CheckIcon className="h-4 w-4 text-blue-500 absolute" style={{ left: '2px' }} />
            </div>
        );
    }
    return null; // Should not happen
};

export function isVCardContact(obj: any): obj is VCardContact {
  return obj && typeof obj === 'object' && (
    typeof obj.name === 'string' ||
    typeof obj.phone === 'string' ||
    typeof obj.email === 'string' ||
    typeof obj.org === 'string' ||
    typeof obj.title === 'string'
  );
}

export function isVCardString(vcard: any): vcard is string {
  return typeof vcard === 'string';
}

// Helper function to safely get contact name
export function getContactName(vcard: string | VCardContact | undefined): string {
    if (!vcard) return 'Неизвестный контакт';
    
    if (isVCardString(vcard)) {
        const parsed = parseVCard(vcard);
        if (parsed.name && !parsed.name.startsWith('BEGIN:VCARD')) {
            return parsed.name;
        }
        
        // Try to extract name from FN field
        const fnMatch = vcard.match(/FN:([^\n]+)/);
        if (fnMatch) {
            return fnMatch[1].trim();
        }
        
        // Try to extract name from N field
        const nMatch = vcard.match(/N:([^\n]+)/);
        if (nMatch) {
            const nameParts = nMatch[1].split(';').filter(part => part.trim());
            if (nameParts.length > 0) {
                return nameParts.join(' ').trim();
            }
        }
        
        return 'Неизвестный контакт';
    } else if (isVCardContact(vcard)) {
        return vcard.name || 'Неизвестный контакт';
    }
    
    return 'Неизвестный контакт';
}

// Helper function to format the last seen timestamp into human-readable string (Russian)
export const formatLastSeen = (timestamp?: number): string => {
    if (!timestamp) return 'Не в сети';

    const now = new Date();
    const lastSeenDate = new Date(timestamp * 1000); // Convert seconds to milliseconds

    const diffSeconds = Math.floor((now.getTime() - lastSeenDate.getTime()) / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);

    const timeFormat: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: false };
    // Russian locale for date formatting
    const locale = 'ru-RU';
    const dateFormat: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' }; // e.g., "8 апр."
    const dateFormatWithYear: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }; // e.g., "8 апр. 2023 г."

    // Function to get the correct declension for minutes/hours
    const getMinutesAgoString = (minutes: number): string => {
        const lastDigit = minutes % 10;
        const lastTwoDigits = minutes % 100;
        if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return `${minutes} минут назад`;
        if (lastDigit === 1) return `${minutes} минуту назад`;
        if (lastDigit >= 2 && lastDigit <= 4) return `${minutes} минуты назад`;
        return `${minutes} минут назад`;
    };

    // --- Same Day ---
    if (now.toDateString() === lastSeenDate.toDateString()) {
        if (diffSeconds < 5) return 'В сети'; // Treat very recent as Online
        if (diffSeconds < 60) return 'был(а) только что';
        if (diffMinutes < 60) return `был(а) ${getMinutesAgoString(diffMinutes)}`;
        return `был(а) сегодня в ${lastSeenDate.toLocaleTimeString(locale, timeFormat)}`;
    }

    // --- Yesterday ---
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (yesterday.toDateString() === lastSeenDate.toDateString()) {
        return `был(а) вчера в ${lastSeenDate.toLocaleTimeString(locale, timeFormat)}`;
    }

    // --- Older Dates ---
    if (now.getFullYear() === lastSeenDate.getFullYear()) {
        // Current year: "был(а) 8 апр. в 15:30"
        return `был(а) ${lastSeenDate.toLocaleDateString(locale, dateFormat)} в ${lastSeenDate.toLocaleTimeString(locale, timeFormat)}`;
    } else {
        // Previous years: "был(а) 8 апр. 2023 г. в 15:30"
        return `был(а) ${lastSeenDate.toLocaleDateString(locale, dateFormatWithYear)} в ${lastSeenDate.toLocaleTimeString(locale, timeFormat)}`;
    }
};

export const getFileTypeCategory = (mimetype: string, filename?: string): string => {
  const cleanMimeType = getCleanMimeType(mimetype, '');

  if (cleanMimeType.startsWith('image/')) return 'image';
  if (cleanMimeType.startsWith('video/')) return 'video';
  if (cleanMimeType.startsWith('audio/')) return 'audio';
  if (cleanMimeType === 'application/pdf') return 'pdf';
  if (cleanMimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'docx';
  if (cleanMimeType.includes('presentation') || filename?.match(/\.pptx?$/i)) return 'presentation';
  if (isSpreadsheetFile(cleanMimeType, filename)) return 'spreadsheet';
  return 'document';
};
